import { ChangeDetectionStrategy, Component, OnInit } from '@angular/core';
import { FormBuilder, ReactiveFormsModule } from '@angular/forms';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { CheckBoxModule } from '@syncfusion/ej2-angular-buttons';
import { CommandClickEventArgs, GridModule } from '@syncfusion/ej2-angular-grids';
import { ToolbarModule } from '@syncfusion/ej2-angular-navigations';
import { DataManager } from '@syncfusion/ej2-data';
import {
  SamplingProfileConfigDTO,
  SamplingProfileType,
  VehicleListDTO,
  VehicleSignalAvailabilityDTO,
} from 'src/app/base/models/models.swagger.generated';
import { EnumFilterService } from 'src/app/base/services/enum-filter.service';
import { DynamicEditFormComponent } from 'src/app/base/shared/dynamic/dynamic-edit-form.component';
import { DynamicGridBaseComponent } from 'src/app/base/shared/grid-base/dynamic-grid-base.component';
import { SearchFieldComponent } from 'src/app/base/shared/search-field/search-field.component';
import { AvpCustomAdaptor } from 'src/app/base/shared/syncfusion/avp-custom-adaptor';
import { GridConstantsFactory } from 'src/app/base/shared/syncfusion/grid-constants-factory';
import { SpinnerService } from 'src/app/base/spinner/spinner.service';
import { SamplingProfileEditModel, samplingProfileDynamicDefinition } from './sampling-profile-dynamic.definition';
import { SamplingProfileSignalsEditorComponent } from './sampling-profile-signals-editor/sampling-profile-signals-editor.component';
import { SamplingProfilesService } from './sampling-profiles.service';
import { SignalCatalogLookupService } from './signal-catalog-lookup.service';

/**
 * Collector configuration: which sampling profiles exist and which signals each one tracks.
 *
 * The grid search deliberately omits each profile's signal list, so opening a row loads the full
 * profile (and its assigned vehicles) before the editor renders.
 */
@Component({
  selector: 'app-sampling-profiles',
  templateUrl: './sampling-profiles.component.html',
  styleUrls: ['./sampling-profiles.component.scss'],
  imports: [
    SearchFieldComponent,
    CheckBoxModule,
    GridModule,
    ToolbarModule,
    DynamicEditFormComponent,
    SamplingProfileSignalsEditorComponent,
    // Needed for the [formGroup]/[formControlName] bindings in the custom signals field template.
    ReactiveFormsModule,
    TranslatePipe,
  ],
  changeDetection: ChangeDetectionStrategy.Eager,
  providers: [SamplingProfilesService, SignalCatalogLookupService],
})
export class SamplingProfilesComponent extends DynamicGridBaseComponent<typeof samplingProfileDynamicDefinition> implements OnInit {

  /** Vehicles running the profile currently open in the editor. */
  public assignedVehicles: VehicleListDTO[] = [];

  /** Vehicle whose availability verdicts the editor is showing. */
  public selectedVehicleId: number | null = null;

  public availability: VehicleSignalAvailabilityDTO | null = null;
  public availabilityLoading = false;

  public isSystemProfile = false;

  constructor(
    private samplingProfilesService: SamplingProfilesService,
    fb: FormBuilder,
    translateService: TranslateService,
    private spinnerService: SpinnerService,
    enumFilterService: EnumFilterService,
  ) {
    super(samplingProfileDynamicDefinition, fb, null, translateService, samplingProfilesService, enumFilterService);
    this.gridCommands = GridConstantsFactory.createStandardGridCommands();
    this.searchOptions = { fields: this.crudDefinition.grid.searchFields };
  }

  public ngOnInit(): void {
    this.data = new DataManager({
      adaptor: new AvpCustomAdaptor(this.samplingProfilesService, this.spinnerService),
    });
    this.editDialogTitle = 'i18n.domain.sampling-profiles.edit-dialog-title';
    this.addDialogTitle = 'i18n.domain.sampling-profiles.add-dialog-title';
  }

  public commandClick(args: CommandClickEventArgs): void {
    // Standard edit/delete commands are handled by the grid itself; nothing custom here yet.
  }

  /** Reloads availability when the user picks a different vehicle in the signal editor. */
  public onSelectedVehicleIdChange(vehicleId: number | null): void {
    this.selectedVehicleId = vehicleId;
    this.availability = null;

    if (!vehicleId) {
      return;
    }

    this.availabilityLoading = true;
    this.samplingProfilesService.getSignalAvailability(vehicleId).subscribe({
      next: availability => {
        this.availability = availability;
        this.availabilityLoading = false;
      },
      error: () => {
        this.availabilityLoading = false;
      },
    });
  }

  protected override addObjectCompare(addedObject: SamplingProfileConfigDTO, rowObject: SamplingProfileConfigDTO): boolean {
    if (this.addIndexFindColumnName) {
      return addedObject.name === rowObject.name;
    }
    return false;
  }

  protected getEditDialogTitleParams(rowData: any): object {
    return { param: rowData?.name };
  }

  /**
   * The grid row carries no signal list (the search endpoint omits it), so start the form with an empty
   * list and fill it in once the full profile has loaded.
   */
  protected override prepareEditFormData(data: any): Partial<SamplingProfileEditModel> {
    return {
      ...(data ?? {}),
      signals: [],
    } as Partial<SamplingProfileEditModel>;
  }

  protected override afterInitializeEditForm(originalData: any): void {
    this.resetAvailabilityContext();

    const profileId = originalData?.id as number | undefined;
    this.isSystemProfile = originalData?.profileType === SamplingProfileType.System;

    if (!profileId) {
      // Adding a new profile — nothing to load, and no vehicle can be assigned to it yet.
      return;
    }

    this.samplingProfilesService.getProfile(profileId).subscribe(profile => {
      if (!profile) {
        return;
      }

      this.isSystemProfile = profile.profileType === SamplingProfileType.System;
      // Patch rather than rebuild so the user's in-progress edits to other fields survive the load.
      this.gridEditForm?.get('signals')?.setValue(profile.signals ?? [], { emitEvent: false });
      this.gridEditForm?.get('signals')?.markAsPristine();
    });

    this.samplingProfilesService.getAssignedVehicles(profileId).subscribe(vehicles => {
      this.assignedVehicles = vehicles ?? [];
      // Default to the first assigned vehicle so availability is visible without an extra click.
      if (this.assignedVehicles.length > 0) {
        this.onSelectedVehicleIdChange(this.assignedVehicles[0].id!);
      }
    });
  }

  private resetAvailabilityContext(): void {
    this.assignedVehicles = [];
    this.selectedVehicleId = null;
    this.availability = null;
    this.availabilityLoading = false;
    this.isSystemProfile = false;
  }
}
